import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { PublisherConfig, SelfServiceFormData } from '../types/selfService';
import {
  checkCampaignFeasibility,
  type CampaignFeasibilityResponse,
} from './feasibilityService';
import { ApiError } from '../lib/apiClient';

const DEBOUNCE_MS = 350;
const SPINNER_DELAY_MS = 200;
// CTV-689 follow-up: a 429 used to drop the verdict AND the error state
// disabled nothing, so rate limiting silently switched the inventory gate
// off. Now the hook auto-retries per the server's Retry-After (bounded),
// holding `loading` true so Continue stays disabled until a verdict lands.
const RETRY_MAX = 2;
const RETRY_FALLBACK_SEC = 5;
const RETRY_CAP_SEC = 30;

export interface UseCampaignFeasibilityResult {
  verdict: CampaignFeasibilityResponse | null;
  loading: boolean;
  error: string | null;
}

interface FeasibilityParams {
  productId: string;
  budget: number;
  startDate: string;
  endDate: string;
  paymentPlan: 'upfront' | 'monthly';
  targetingMode: 'default' | 'custom';
  geoTargetZips?: string[];
  geoTargetDMAs?: string[];
  geoTargetStates?: string[];
  excludeSubmissionId?: string;
}

/**
 * Live feasibility hook for the Campaign Setup step. Fires `/inventory/
 * campaign-feasibility` whenever budget / dates / payment plan / targeting
 * inputs are all valid; debounced 350ms; aborts in-flight requests on input
 * change; guards against out-of-order responses; defers the spinner so a
 * sub-200ms response doesn't flicker.
 *
 * The hook does NOT block continue/submit on its own — it only surfaces a
 * verdict. The wizard reads `verdict.feasible` and (CTV-474) per-flight
 * `constraintKind` / `geoShortfall` to render the warning card.
 */
export function useCampaignFeasibility(
  config: PublisherConfig,
  excludeSubmissionId?: string,
): UseCampaignFeasibilityResult & {
  recheck: () => Promise<CampaignFeasibilityResponse | null>;
} {
  const { watch } = useFormContext<SelfServiceFormData>();
  const budget = watch('budget');
  const startDate = watch('startDate');
  const endDate = watch('endDate');
  const paymentPlan = watch('paymentPlan');
  const targetingMode = watch('targetingMode');
  const geoMode = watch('geoMode');
  const radiusZips = watch('geoTargetZips');
  const explicitDMAs = watch('selectedDMAs' as keyof SelfServiceFormData) as unknown as string[] | undefined;
  const explicitStates = watch('selectedStates' as keyof SelfServiceFormData) as unknown as string[] | undefined;

  // Resolve the active ZIP list for the inventory check.
  // CTV-502: ZIP targeting now flows through radius mode only — the
  // standalone selectedZIPs arm was removed with the ZIPs tab.
  const effectiveZips = useMemo(() => {
    if (targetingMode !== 'custom') return undefined;
    if (geoMode === 'radius' && Array.isArray(radiusZips) && radiusZips.length > 0) {
      return radiusZips;
    }
    return undefined;
  }, [targetingMode, geoMode, radiusZips]);

  // CTV-474: DMA + State geo modes now route through the geo-aware feasibility
  // model. Each is only populated when its mode is active.
  const effectiveDMAs = useMemo(() => {
    if (targetingMode !== 'custom' || geoMode !== 'dmas') return undefined;
    return Array.isArray(explicitDMAs) && explicitDMAs.length > 0 ? explicitDMAs : undefined;
  }, [targetingMode, geoMode, explicitDMAs]);

  const effectiveStates = useMemo(() => {
    if (targetingMode !== 'custom' || geoMode !== 'states') return undefined;
    return Array.isArray(explicitStates) && explicitStates.length > 0 ? explicitStates : undefined;
  }, [targetingMode, geoMode, explicitStates]);

  const productId = config.products[0]?.id;

  // Stable serialized key — avoids re-fires on reference equality of identical
  // arrays. Each list is sorted so [10001, 30303] and [30303, 10001] hash equal.
  const inputsKey = useMemo(
    () =>
      JSON.stringify({
        productId,
        budget,
        startDate,
        endDate,
        paymentPlan,
        targetingMode,
        geoTargetZips: effectiveZips ? [...effectiveZips].sort() : null,
        geoTargetDMAs: effectiveDMAs ? [...effectiveDMAs].sort() : null,
        geoTargetStates: effectiveStates ? [...effectiveStates].sort() : null,
        excludeSubmissionId,
      }),
    [
      productId,
      budget,
      startDate,
      endDate,
      paymentPlan,
      targetingMode,
      effectiveZips,
      effectiveDMAs,
      effectiveStates,
      excludeSubmissionId,
    ],
  );

  const [verdict, setVerdict] = useState<CampaignFeasibilityResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestIdRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const spinnerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Gate: all required inputs must be valid before firing. Payment plan is
  // not part of the gate — the buyer only picks it on the review step, but
  // the campaign step needs live feasibility immediately ('upfront' default;
  // monthly doesn't change inventory math). The custom-with-no-selection case
  // (CTV-570 v2) also gates: the backend would silently evaluate station-wide
  // default and produce a misleading verdict.
  const gateOk =
    !!productId &&
    !!budget &&
    budget > 0 &&
    !!startDate &&
    !!endDate &&
    endDate > startDate &&
    !(targetingMode === 'custom' && !effectiveZips && !effectiveDMAs && !effectiveStates);

  // Latest valid request params, readable by fire()/recheck() outside the
  // effect without re-creating callbacks per keystroke.
  const paramsRef = useRef<FeasibilityParams | null>(null);
  paramsRef.current = gateOk
    ? {
        productId: productId!,
        budget: budget!,
        startDate,
        endDate,
        paymentPlan: paymentPlan ?? 'upfront',
        targetingMode: targetingMode ?? 'default',
        geoTargetZips: effectiveZips,
        geoTargetDMAs: effectiveDMAs,
        geoTargetStates: effectiveStates,
        excludeSubmissionId,
      }
    : null;

  // One check attempt. `attempt` counts 429 auto-retries already burned for
  // the current inputs; a rate-limited response with retries remaining keeps
  // `loading` true (Continue stays disabled) and re-fires after Retry-After.
  const fire = useCallback((attempt: number): Promise<CampaignFeasibilityResponse | null> => {
    const p = paramsRef.current;
    if (!p) return Promise.resolve(null);
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const reqId = ++requestIdRef.current;

    // Defer spinner: avoids UI flicker for sub-200ms responses.
    if (spinnerTimerRef.current) clearTimeout(spinnerTimerRef.current);
    spinnerTimerRef.current = setTimeout(() => {
      if (reqId === requestIdRef.current) setLoading(true);
    }, SPINNER_DELAY_MS);

    return checkCampaignFeasibility(
      {
        ...p,
        // Dev-only: ask the API for the per-line-item booked breakdown.
        // Prod builds drop this flag at the bundler.
        includeDiagnostic: import.meta.env.DEV,
      },
      ctrl.signal,
    )
      .then((data) => {
        if (reqId !== requestIdRef.current) return null;
        setVerdict(data);
        setError(null);
        setLoading(false);
        return data;
      })
      .catch((e) => {
        if (reqId !== requestIdRef.current) return null;
        const name = (e as { name?: string })?.name;
        if (name === 'AbortError' || name === 'CanceledError') return null;
        const rateLimited = e instanceof ApiError && e.status === 429;
        if (rateLimited && attempt < RETRY_MAX) {
          const delaySec = Math.min(
            (e as ApiError).retryAfterSec ?? RETRY_FALLBACK_SEC,
            RETRY_CAP_SEC,
          );
          setError(null);
          setLoading(true);
          if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
          retryTimerRef.current = setTimeout(() => {
            void fire(attempt + 1);
          }, delaySec * 1000);
          return null;
        }
        // Rate-limited (429) is "slow down", not "broken"; say so.
        setError(
          rateLimited
            ? 'You are making changes quickly. Give it a few seconds and adjust again.'
            : 'We could not check inventory just now. Try again in a moment.',
        );
        setVerdict(null);
        setLoading(false);
        return null;
      })
      .finally(() => {
        if (spinnerTimerRef.current) {
          clearTimeout(spinnerTimerRef.current);
          spinnerTimerRef.current = null;
        }
      });
  }, []);

  useEffect(() => {
    if (!paramsRef.current) {
      setVerdict(null);
      setLoading(false);
      setError(null);
      return;
    }

    // CTV-474: clear the verdict immediately on input change so the warning
    // banner animates *out* before the debounced request fires. It comes
    // back when the new verdict arrives and is still negative.
    setVerdict(null);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void fire(0);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      // A pending 429 retry belongs to the previous inputs; drop it. The
      // effect re-run schedules a fresh check for the new inputs.
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputsKey]);

  // Imperative one-shot for the wizard's Continue gate: when the live check
  // previously failed, Continue re-checks blocking and gates on THIS result
  // (not the possibly-stale lifted state). No debounce, no auto-retries — the
  // buyer wants an answer now; a 429 here resolves to null and the gate blocks.
  const recheck = useCallback((): Promise<CampaignFeasibilityResponse | null> => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    return fire(RETRY_MAX);
  }, [fire]);

  return { verdict, loading, error, recheck };
}
